import React, { useMemo } from 'react';
import { Text, Group, Skeleton, useMantineTheme } from '@mantine/core';

interface ScopeLineProps {
  campaigns: string[];
  sources?: string[];
  reportName?: string;
  primaryGoal: string;
  revenueGoal?: string;
  campaignOptions: Array<{ value: string; label: string }>;
  loading?: boolean;
}

export function ScopeLine({
  campaigns,
  sources = [],
  reportName = '',
  primaryGoal,
  revenueGoal = '',
  campaignOptions,
  loading = false,
}: ScopeLineProps) {
  const campaignNames = useMemo(() => {
    const normalizedCampaigns = campaigns.map((id) => String(id));
    if (campaigns.length === 0) return 'No campaigns selected';
    if (campaigns.length <= 2) {
      return normalizedCampaigns
        .map((id) => campaignOptions.find((c) => String(c.value) === id)?.label || id)
        .join(', ');
    }
    return `Multiple campaigns`;
  }, [campaigns, campaignOptions]);

  const sourceDisplay = useMemo(() => {
    if (sources && sources.length > 0) {
      return sources.slice(0, 3).join(', ') + (sources.length > 3 ? ` +${sources.length - 3}` : '');
    }
    return 'All sources';
  }, [sources]);

  const reportDisplay = reportName || 'Not configured';
  const revenueDisplay = revenueGoal || 'Not configured';

  const theme = useMantineTheme();
  const dk = theme.colorScheme === 'dark';

  if (loading) {
    return (
      <Group
        spacing="xl"
        style={{
          padding: '10px 22px',
          borderBottom: `1px solid ${dk ? '#2C2E33' : '#EBEBEB'}`,
          background: dk ? '#1A1B1E' : '#FAFAF8',
          fontSize: 12,
        }}
      >
        <Skeleton height={14} width={180} radius="sm" />
        <Skeleton height={14} width={140} radius="sm" />
        <Skeleton height={14} width={160} radius="sm" />
        <Skeleton height={14} width={120} radius="sm" />
        <Skeleton height={14} width={170} radius="sm" />
      </Group>
    );
  }

  return (
    <Group spacing="xl" style={{ padding: '10px 22px', borderBottom: `1px solid ${dk ? '#2C2E33' : '#EBEBEB'}`, background: dk ? '#1A1B1E' : '#FAFAF8', fontSize: 12 }}>
      <Group spacing="xs">
        <Text size="sm" color="dimmed" weight={500}>
          Showing: {campaignNames}
        </Text>
      </Group>
      <Group spacing="xs">
        <Text size="sm" color="dimmed">
          ·
        </Text>
        <Text size="sm" color="dimmed">
          Sources: {sourceDisplay}
        </Text>
      </Group>
      <Group spacing="xs">
        <Text size="sm" color="dimmed">
          ·
        </Text>
        <Text size="sm" color="dimmed">
          Report: {reportDisplay}
        </Text>
      </Group>
      <Group spacing="xs">
        <Text size="sm" color="dimmed">
          ·
        </Text>
        <Text size="sm" color="dimmed">
          Goal: {primaryGoal}
        </Text>
      </Group>
      <Group spacing="xs">
        <Text size="sm" color="dimmed">
          ·
        </Text>
        <Text size="sm" color="dimmed">
          Revenue event: {revenueDisplay}
        </Text>
      </Group>
    </Group>
  );
}
